import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Plus, Edit, Trash2, UserCircle, Upload, Download, CheckCircle2, XCircle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";

const AdminPlayers = () => {
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [filterTeam, setFilterTeam] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Bulk-import state
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [csvRows, setCsvRows] = useState([]);
  const [csvFileName, setCsvFileName] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    team_id: "",
    team_ids: [],
    position: "",
    number: "",
    date_of_birth: "",
    nationality: "Antigua and Barbuda",
    photo_url: "",
    height: "",
    weight: ""
  });

  const fetchData = async () => {
    try {
      const [playersRes, teamsRes] = await Promise.all([
        axios.get(`${API}/players`),
        axios.get(`${API}/teams`)
      ]);
      setPlayers(playersRes.data);
      setTeams(teamsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const teamsMap = {};
  teams.forEach(t => { teamsMap[t.team_id] = t; });

  const filteredPlayers = (filterTeam === "all"
    ? players
    : players.filter(p => p.team_id === filterTeam || (p.team_ids || []).includes(filterTeam))
  ).filter(p => {
    if (!searchQuery.trim()) return true;
    return p.name.toLowerCase().includes(searchQuery.trim().toLowerCase());
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Ensure team_ids always includes the primary team_id
      const team_ids = Array.from(new Set([formData.team_id, ...(formData.team_ids || [])])).filter(Boolean);
      const payload = {
        ...formData,
        name: (formData.name || "").trim(),
        team_ids,
        number: formData.number ? parseInt(formData.number) : null
      };

      let created = null;
      if (editingPlayer) {
        const res = await axios.put(`${API}/players/${editingPlayer.player_id}`, payload, { withCredentials: true });
        created = res.data;
        toast.success("Player updated");
      } else {
        try {
          const res = await axios.post(`${API}/players`, payload, { withCredentials: true });
          created = res.data;
          toast.success(`Added ${created?.name || payload.name}`);
        } catch (err) {
          if (err.response?.status === 409 && err.response?.data?.detail?.clashes) {
            const clashes = err.response.data.detail.clashes;
            const lines = clashes.map(c => `• ${c.name}${c.registration_id ? ` [${c.registration_id}]` : ""} — ${c.reasons.join(", ")}`).join("\n");
            if (window.confirm(`A similar player already exists:\n\n${lines}\n\nCreate anyway?`)) {
              const res = await axios.post(`${API}/players?force=true`, payload, { withCredentials: true });
              created = res.data;
              toast.success(`Added ${created?.name || payload.name} (forced)`);
            } else {
              return;
            }
          } else {
            throw err;
          }
        }
      }

      // Refresh BEFORE closing so UI stays consistent
      await fetchData();

      // If the user has filtered to a different team, auto-switch so they can
      // actually see the new player (fixes "nothing shows after create" complaint).
      if (created && filterTeam !== "all" && created.team_id && filterTeam !== created.team_id) {
        setFilterTeam(created.team_id);
      }
      // Clear any admin search so the new player isn't hidden by a stale filter.
      setSearchQuery("");

      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error("Error saving player:", error);
      toast.error(error.response?.data?.detail || "Failed to save player");
    }
  };

  const handleEdit = (player) => {
    setEditingPlayer(player);
    setFormData({
      name: player.name || "",
      team_id: player.team_id || "",
      team_ids: player.team_ids && player.team_ids.length ? player.team_ids : (player.team_id ? [player.team_id] : []),
      position: player.position || "",
      number: player.number || "",
      date_of_birth: player.date_of_birth || "",
      nationality: player.nationality || "Antigua and Barbuda",
      photo_url: player.photo_url || "",
      height: player.height || "",
      weight: player.weight || ""
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (playerId) => {
    if (!window.confirm("Are you sure you want to delete this player?")) return;
    
    try {
      await axios.delete(`${API}/players/${playerId}`, { withCredentials: true });
      toast.success("Player deleted successfully");
      fetchData();
    } catch (error) {
      console.error("Error deleting player:", error);
      toast.error("Failed to delete player");
    }
  };

  const resetForm = () => {
    setEditingPlayer(null);
    setFormData({
      name: "",
      team_id: "",
      team_ids: [],
      position: "",
      number: "",
      date_of_birth: "",
      nationality: "Antigua and Barbuda",
      photo_url: "",
      height: "",
      weight: ""
    });
  };

  // -------------- BULK IMPORT --------------
  // Simple CSV parser supporting quoted values and commas inside quotes.
  const parseCsv = (text) => {
    const lines = text.replace(/\r/g, "").split("\n").filter((l) => l.trim());
    if (!lines.length) return [];
    const parseLine = (line) => {
      const out = [];
      let cur = "";
      let inQ = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
          else inQ = !inQ;
        } else if (ch === "," && !inQ) {
          out.push(cur.trim());
          cur = "";
        } else {
          cur += ch;
        }
      }
      out.push(cur.trim());
      return out;
    };
    const header = parseLine(lines[0]).map((h) => h.toLowerCase().replace(/\s+/g, "_"));
    return lines.slice(1).map((line) => {
      const cells = parseLine(line);
      const row = {};
      header.forEach((h, i) => { row[h] = cells[i] ?? ""; });
      return row;
    });
  };

  const handleCsvFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);
    setBulkResults(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const rows = parseCsv(ev.target.result || "");
      setCsvRows(rows);
      if (!rows.length) toast.error("CSV parsed but contained no data rows");
    };
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    const csv = "name,team_short_name,position,number,date_of_birth,nationality,height,weight,photo_url\n" +
                "John Doe,ASU,GK,1,1998-05-20,Antigua and Barbuda,1.85 m,78 kg,\n" +
                "Jane Smith,PAR,MID,10,2001-11-08,Antigua and Barbuda,1.72 m,68 kg,\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "abfa-players-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const submitBulk = async () => {
    if (!csvRows.length) {
      toast.error("Load a CSV file first");
      return;
    }
    setBulkLoading(true);
    setBulkResults(null);
    try {
      const payload = {
        players: csvRows.map((r) => ({
          name: r.name || "",
          team_name: r.team_name || null,
          team_short_name: r.team_short_name || null,
          position: (r.position || "").toUpperCase(),
          number: r.number ? parseInt(r.number, 10) : null,
          date_of_birth: r.date_of_birth || null,
          nationality: r.nationality || "Antigua and Barbuda",
          height: r.height || null,
          weight: r.weight || null,
          photo_url: r.photo_url || null,
        })),
      };
      const { data } = await axios.post(`${API}/players/bulk`, payload, { withCredentials: true });
      setBulkResults(data);
      if (data.created.length > 0) {
        toast.success(`${data.created.length} of ${data.total} players imported`);
        fetchData();
      }
      if (data.errors.length > 0) {
        toast.warning(`${data.errors.length} rows had errors — see details below`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk import failed");
    } finally {
      setBulkLoading(false);
    }
  };

  const resetBulk = () => {
    setCsvRows([]);
    setCsvFileName("");
    setBulkResults(null);
  };

  return (
    <AdminLayout title="Players">
      <div data-testid="admin-players">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4 flex-wrap">
            <p className="text-gray-500">Manage all registered players</p>
            <Select value={filterTeam} onValueChange={setFilterTeam}>
              <SelectTrigger className="w-48 rounded-none" data-testid="filter-team-select">
                <SelectValue placeholder="All Teams" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Teams</SelectItem>
                {teams.map(team => (
                  <SelectItem key={team.team_id} value={team.team_id}>{team.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="search"
              placeholder="Search by name…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-56 rounded-none"
              data-testid="admin-players-search"
            />
            <span className="text-xs text-gray-500 font-semibold uppercase tracking-widest" data-testid="players-count">
              {filteredPlayers.length} / {players.length}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
          <Dialog open={isBulkOpen} onOpenChange={(open) => { setIsBulkOpen(open); if (!open) resetBulk(); }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-none" data-testid="bulk-import-players-btn">
                <Upload className="w-4 h-4 mr-2" /> Bulk Import
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading font-bold uppercase">Bulk Import Players</DialogTitle>
                <DialogDescription>
                  Upload a CSV of players. Each row auto-gets an ABFA registration ID and initial club history entry.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="p-4 bg-blue-50 border-l-4 border-blue-400 text-sm">
                  <p className="font-heading uppercase text-xs font-bold text-blue-900 mb-1">Required columns</p>
                  <p className="text-blue-800"><code className="bg-white px-1">name</code>, <code className="bg-white px-1">position</code> (GK/DEF/MID/FWD), and either <code className="bg-white px-1">team_short_name</code> or <code className="bg-white px-1">team_name</code>.</p>
                  <p className="mt-1 text-blue-700 text-xs">Optional: <code className="bg-white px-1">number, date_of_birth, nationality, height, weight, photo_url</code>.</p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <Button variant="outline" onClick={downloadTemplate} className="rounded-none" data-testid="btn-download-template">
                    <Download className="w-4 h-4 mr-2" /> Download Template
                  </Button>
                  <label className="cursor-pointer">
                    <input type="file" accept=".csv" onChange={handleCsvFile} className="hidden" data-testid="input-csv-file" />
                    <span className="inline-flex items-center px-4 py-2 bg-[#FFC72C] hover:bg-[#e6b325] text-black font-heading uppercase text-sm">
                      <FileText className="w-4 h-4 mr-2" /> Choose CSV
                    </span>
                  </label>
                  {csvFileName && (
                    <span className="text-sm text-gray-600" data-testid="text-csv-file-name">
                      {csvFileName} · {csvRows.length} row{csvRows.length === 1 ? "" : "s"}
                    </span>
                  )}
                </div>

                {csvRows.length > 0 && !bulkResults && (
                  <div className="border border-gray-200 max-h-56 overflow-y-auto" data-testid="csv-preview">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0 uppercase text-[10px]">
                        <tr>
                          <th className="px-3 py-2 text-left">#</th>
                          <th className="px-3 py-2 text-left">Name</th>
                          <th className="px-3 py-2 text-left">Team</th>
                          <th className="px-3 py-2 text-left">Pos</th>
                          <th className="px-3 py-2 text-left">#</th>
                        </tr>
                      </thead>
                      <tbody>
                        {csvRows.slice(0, 50).map((r, i) => (
                          <tr key={i} className="border-t border-gray-100">
                            <td className="px-3 py-1.5 text-gray-500">{i + 1}</td>
                            <td className="px-3 py-1.5 font-semibold">{r.name || <span className="text-red-500">(missing)</span>}</td>
                            <td className="px-3 py-1.5">{r.team_short_name || r.team_name || <span className="text-red-500">(missing)</span>}</td>
                            <td className="px-3 py-1.5">{r.position || <span className="text-red-500">(missing)</span>}</td>
                            <td className="px-3 py-1.5">{r.number || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {csvRows.length > 50 && <p className="text-xs text-gray-500 p-2">Showing first 50 of {csvRows.length} rows</p>}
                  </div>
                )}

                {bulkResults && (
                  <div className="space-y-3" data-testid="bulk-results">
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div className="p-3 bg-gray-50">
                        <p className="font-heading text-2xl font-bold">{bulkResults.total}</p>
                        <p className="text-xs uppercase text-gray-500">Total Rows</p>
                      </div>
                      <div className="p-3 bg-green-50">
                        <p className="font-heading text-2xl font-bold text-[#009E60]">{bulkResults.created.length}</p>
                        <p className="text-xs uppercase text-green-700">Imported</p>
                      </div>
                      <div className="p-3 bg-red-50">
                        <p className="font-heading text-2xl font-bold text-red-500">{bulkResults.errors.length}</p>
                        <p className="text-xs uppercase text-red-700">Errors</p>
                      </div>
                    </div>

                    {bulkResults.created.length > 0 && (
                      <details className="border border-green-200 p-3" open>
                        <summary className="cursor-pointer font-heading uppercase text-xs text-[#009E60]">
                          <CheckCircle2 className="w-3 h-3 inline mr-1" /> {bulkResults.created.length} created
                        </summary>
                        <ul className="mt-2 space-y-0.5 text-xs max-h-40 overflow-y-auto">
                          {bulkResults.created.map((c) => (
                            <li key={c.player_id} className="text-gray-700">
                              Row {c.row}: <strong>{c.name}</strong> → {c.team_name} · <code className="font-mono text-[10px] bg-gray-100 px-1">{c.registration_id}</code>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}

                    {bulkResults.errors.length > 0 && (
                      <details className="border border-red-200 p-3" open>
                        <summary className="cursor-pointer font-heading uppercase text-xs text-red-500">
                          <XCircle className="w-3 h-3 inline mr-1" /> {bulkResults.errors.length} errors
                        </summary>
                        <ul className="mt-2 space-y-0.5 text-xs max-h-40 overflow-y-auto">
                          {bulkResults.errors.map((e, i) => (
                            <li key={i} className="text-red-700">
                              Row {e.row}: <strong>{e.name || "(no name)"}</strong> — {e.error}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}

                    <Button variant="outline" onClick={resetBulk} className="rounded-none w-full">
                      Import another file
                    </Button>
                  </div>
                )}

                {csvRows.length > 0 && !bulkResults && (
                  <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
                    <Button variant="outline" onClick={resetBulk} className="rounded-none">Clear</Button>
                    <Button
                      onClick={submitBulk}
                      disabled={bulkLoading}
                      className="bg-[#009E60] hover:bg-[#00834f] text-white rounded-none"
                      data-testid="btn-submit-bulk"
                    >
                      {bulkLoading ? "Importing…" : `Import ${csvRows.length} player${csvRows.length === 1 ? "" : "s"}`}
                    </Button>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#FFC72C] hover:bg-[#E6B325] text-black rounded-none" data-testid="add-player-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Player
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading font-bold uppercase">
                  {editingPlayer ? "Edit Player" : "Add New Player"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="player-form">
                <div>
                  <label className="text-sm font-medium">Player Name *</label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="rounded-none"
                    data-testid="player-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Primary Team (Club) *</label>
                    <Select
                      value={formData.team_id}
                      onValueChange={(v) => setFormData((f) => ({
                        ...f,
                        team_id: v,
                        // Ensure the new primary is present in team_ids
                        team_ids: Array.from(new Set([v, ...(f.team_ids || [])])).filter(Boolean),
                      }))}
                    >
                      <SelectTrigger className="rounded-none" data-testid="player-team-select">
                        <SelectValue placeholder="Select Team" />
                      </SelectTrigger>
                      <SelectContent>
                        {teams.map(team => (
                          <SelectItem key={team.team_id} value={team.team_id}>{team.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Position *</label>
                    <Select value={formData.position} onValueChange={(v) => setFormData({...formData, position: v})}>
                      <SelectTrigger className="rounded-none" data-testid="player-position-select">
                        <SelectValue placeholder="Select Position" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GK">Goalkeeper</SelectItem>
                        <SelectItem value="DEF">Defender</SelectItem>
                        <SelectItem value="MID">Midfielder</SelectItem>
                        <SelectItem value="FWD">Forward</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Additional teams (multi-select) */}
                <div>
                  <label className="text-sm font-medium">Additional Teams</label>
                  <p className="text-xs text-gray-500 mb-2">Select any extra teams the player represents (e.g. national team).</p>
                  <div className="border border-gray-200 max-h-40 overflow-y-auto p-2 space-y-1 bg-gray-50" data-testid="additional-teams-list">
                    {teams
                      .filter((t) => t.team_id !== formData.team_id)
                      .map((t) => {
                        const checked = (formData.team_ids || []).includes(t.team_id);
                        return (
                          <label
                            key={t.team_id}
                            className={`flex items-center gap-2 px-2 py-1 cursor-pointer text-sm ${checked ? "bg-white" : "hover:bg-white"}`}
                            data-testid={`additional-team-${t.team_id}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const nextIds = e.target.checked
                                  ? [...(formData.team_ids || []), t.team_id]
                                  : (formData.team_ids || []).filter((x) => x !== t.team_id);
                                setFormData((f) => ({ ...f, team_ids: nextIds }));
                              }}
                              className="accent-[#009E60]"
                              data-testid={`additional-team-cb-${t.team_id}`}
                            />
                            {t.logo_url && (
                              <img src={t.logo_url} alt="" className="w-5 h-5 object-contain" />
                            )}
                            <span className="flex-1">{t.name}</span>
                            <span className="text-xs text-gray-400 font-mono">{t.short_name}</span>
                          </label>
                        );
                      })}
                    {teams.filter((t) => t.team_id !== formData.team_id).length === 0 && (
                      <p className="text-xs text-gray-400 italic p-2">No other teams available.</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Jersey Number</label>
                    <Input
                      name="number"
                      type="number"
                      value={formData.number}
                      onChange={handleChange}
                      className="rounded-none"
                      data-testid="player-number-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Nationality</label>
                    <Input
                      name="nationality"
                      value={formData.nationality}
                      onChange={handleChange}
                      className="rounded-none"
                      data-testid="player-nationality-input"
                    />
                  </div>
                </div>
                <ImageUploader
                  value={formData.photo_url}
                  onChange={(url) => setFormData({ ...formData, photo_url: url })}
                  category="player-photo"
                  label="Photo"
                  previewShape="square"
                  testId="player-photo"
                />
                <div className="flex gap-4">
                  <Button type="submit" className="flex-1 bg-[#FFC72C] hover:bg-[#E6B325] text-black rounded-none" data-testid="player-submit-btn">
                    {editingPlayer ? "Update Player" : "Create Player"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-none">
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Players Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {[1,2,3,4,5,6,7,8,9,10].map(i => (
              <div key={i} className="h-64 bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : filteredPlayers.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {filteredPlayers.map(player => (
              <div key={player.player_id} className="bg-white border border-gray-200 overflow-hidden group" data-testid={`admin-player-card-${player.player_id}`}>
                <div className="aspect-square bg-gray-100 relative overflow-hidden">
                  <img 
                    src={player.photo_url || 'https://placehold.co/300x300/09090b/FFF?text=PLAYER'} 
                    alt={player.name}
                    className="w-full h-full object-cover"
                  />
                  {player.number && (
                    <span className="absolute top-2 right-2 bg-[#FFC72C] text-black font-heading font-bold text-lg px-2 py-1">
                      {player.number}
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-heading font-bold uppercase text-[#09090b] truncate">{player.name}</h3>
                  <p className="text-sm text-gray-500">{player.position} • {teamsMap[player.team_id]?.short_name || 'N/A'}</p>
                  <div className="flex gap-2 mt-3 md:opacity-0 md:group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(player)}
                      className="flex-1 rounded-none"
                      data-testid={`edit-player-${player.player_id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(player.player_id)}
                      className="rounded-none"
                      data-testid={`delete-player-${player.player_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-white border border-gray-200">
            <UserCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-gray-400 uppercase mb-2">No Players Yet</h3>
            <p className="text-gray-500 mb-4">Add your first player to get started.</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPlayers;
